import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Check for ?direct=true to skip 2FA
  const params = new URLSearchParams(window.location.search);
  const directLogin = params.get('direct') === 'true';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = directLogin ? `${API}/admin/login?skip_2fa=true` : `${API}/admin/login`;
      const { data } = await axios.post(url, { email, password }, { withCredentials: true });
      if (data.requires_2fa) {
        setStep('code');
        toast.success('Verification code sent to your email');
      } else {
        if (data.access_token) {
          localStorage.setItem('admin_token', data.access_token);
          localStorage.setItem('admin_refresh_token', data.refresh_token);
        }
        await refreshUser();
        toast.success('Welcome back, Admin');
        navigate('/admin');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/verify-2fa`, { email, code }, { withCredentials: true });
      if (data.message === 'Login successful' && data.access_token) {
        // Store token in localStorage for cross-domain support
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_refresh_token', data.refresh_token);
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        await refreshUser();
        toast.success('Welcome back, Admin');
        navigate('/admin');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#00F0FF]/10 rounded-2xl flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#00F0FF]" />
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Access
          </h1>
          <p className="text-[#A1A7B5] text-sm mt-1">
            {step === 'email' ? 'Enter your admin credentials' : 'Enter the verification code sent to your email'}
          </p>
        </div>

        <div className="glass-card p-8">
          {step === 'email' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" required className="input-field w-full pl-10" data-testid="admin-login-email" />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
              <div className="relative">
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="input-field w-full pl-10" data-testid="admin-login-password" />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
              <Button type="submit" disabled={loading} className="btn-primary w-full py-3" data-testid="admin-login-submit">
                {loading ? 'Verifying...' : 'Continue'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="bg-[#111621] border border-[#1E2532] rounded-lg p-4 text-center mb-2">
                <p className="text-[#A1A7B5] text-sm">Code sent to</p>
                <p className="text-white font-medium">{email}</p>
              </div>
              <input
                type="text" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code" required maxLength={6}
                className="input-field w-full text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus data-testid="admin-2fa-code"
              />
              <Button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full py-3" data-testid="admin-2fa-submit">
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
              <button type="button" onClick={() => { setStep('email'); setCode(''); }} className="w-full text-center text-[#A1A7B5] text-sm hover:text-white transition-colors">
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
